import { ChangeDetectorRef, Component, EventEmitter, HostListener, Input, Output, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { CommonService } from '../../service/common.service';
import { debounceTime, distinctUntilChanged, firstValueFrom, Subject, Subscription } from 'rxjs';
import { StaffingService } from 'src/app/staffing/staffing.service';
import { ApiResponse } from '../../model/response-format';

export interface FilterState {
  mappingStatus?: 'mapped' | 'unassigned' | null;
  agingDays?: number;              
  source?: string | null;
  industry?: string | null;
  noticePeriod?: '15' | '30' | '60' | '90' | null;
  recommendedStatus?: 'open' | 'low' | 'high' | null;
  vertical?: string | null;
  costCentre?: string | null;
  client?: string | null;
  function?: string | null;
}

export type FilterType = 'checkbox' | 'range' | 'select-string' | 'select-object' | 'radio' | 'text' | 'date';

export interface FilterConfig {
  key: string;              
  label: string;            
  type: FilterType;         
  required?: boolean;       
  placeholder?: string;     
  unit?: string;       
  options?: any[];   
  openDropdownUp? : boolean; 
  pattern?: string | RegExp;        
  patternErrorMessage?: string;    
  hasLineBreak?: boolean;   
  objectOptions?: any[];    
  valueKey?: string;        
  displayKey?: string;      
  isSearchInputVisible?: boolean; 
  searchOnJson?: boolean;
  searchPlaceHolderText?: string | null;
  isDisabled?: boolean;
  rangeOptions?: {
    min: number;
    max: number;
    step: number;
    isDisabled?: boolean;
  };
}

@Component({
  selector: 'app-staffing-filter',
  templateUrl: './staffing-filter.component.html',
  styleUrls: ['./staffing-filter.component.scss'],
  standalone: false
})
export class StaffingFilterComponent implements OnChanges, OnDestroy {
  private _activeLogicCombinationText: string = '';
  @Input() config: FilterConfig[] = [];
  @Input() initialValues: any = {};
  // AI Matching also resets sorting and pagination when the filter form is empty.
  @Input() allowResetWithoutFilters = false;
  @Input() activeLogicCombinationText?: string = '';
  @Input() operatorMessage: string = '';
  @Input() screenName: string = '';
  @Input() srNumber?: string;
  @Input() fetchedSavedFilters: any; 
  @Output() filtersChange = new EventEmitter<any>();
  @Output() reset = new EventEmitter<void>();
  
  openDropdownKey: string | null = null; 
  private searchSubject = new Subject<{ key: string, value: string }>();
  localSearchValues: Record<string, string> = {};
  
  form!: FormGroup;
  loading: boolean = false;
  
  // ✨ NEW: State Tracking Variables for detecting Form Changes
  isChanged: boolean = false;
  private baseFormStateStr: string = '';
  private formSubscription?: Subscription;

  private filterPayloadTemplate: any = {
    SR_VIEW: { spoc: [], country: [], city: [], grade: [], vertical: [], costCentre: [], status: [], statusDetails: [], designation: [], function: [], client: [], skill: [] },
    BENCH_VIEW: { country: [], city: [], skill: [], grade: [], department: [], function: [], designation: [] },
    PM_VIEW: { spoc: [], country: [], city: [], grade: [], vertical: [], costCentre: [], status: [], statusDetails: [], designation: [], function: [], client: [], skill: [] },
  };

  constructor(private fb: FormBuilder, private cd: ChangeDetectorRef, public commonService: CommonService, private _staffingService: StaffingService) {}

  ngOnInit() {
    this.buildForm();
    this.commonService.filterCombinationTextOnAiMactchingScreen.subscribe(data => {
      this.activeLogicCombinationText = data;
    });
    this.searchSubject.pipe(
      debounceTime(800), 
      distinctUntilChanged((prev, curr) => prev.key === curr.key && prev.value === curr.value)
    ).subscribe(({ key, value }) => {
      this.executeSearch(key, value);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    let formNeedsRebuild = false;

    if (changes['config'] && !changes['config'].firstChange) {
      this.config = changes['config'].currentValue || [];
      this.localSearchValues = {};
      formNeedsRebuild = true;
    }

    if (changes['initialValues']) {
      this.initialValues = changes['initialValues'].currentValue || {};
      if (this.form && !formNeedsRebuild) {
        this.safelyPatchFormValues(this.initialValues);
      } else if (!this.form && !changes['initialValues'].firstChange) {
        formNeedsRebuild = true;
      }
    }

    // React to fetchedSavedFilters changes from parent
    if (changes['fetchedSavedFilters']) {
      this.checkChanges();
    }

    if (formNeedsRebuild) {
      this.buildForm();
    }
  }

  // ✨ NEW: Normalizes JSON to accurately diff default min/max ranges, nulls, and empty arrays
  // ✨ UPDATED: Normalizes JSON so null, "", and [] are all treated identically
  private serializeState(val: any): string {
    if (!val) return JSON.stringify({});
    const cleaned: any = {};
    
    this.config?.forEach(ctrl => {
      const key = ctrl.key;
      let value = val[key];

      if (ctrl.type === 'range') {
        const min = ctrl.rangeOptions?.min ?? 0;
        const max = ctrl.rangeOptions?.max ?? 100;
        const fromVal = value?.from !== undefined ? value.from : min;
        const toVal = value?.to !== undefined ? value.to : max;
        cleaned[key] = { from: fromVal, to: toVal };
      } 
      else if (Array.isArray(value)) {
        // ✨ FIX: If the array is empty, treat it as null instead of []
        cleaned[key] = value.length > 0 ? [...value].sort() : null; 
      } 
      else {
        // ✨ FIX: Treat null, undefined, and empty strings all as null
        cleaned[key] = (value === null || value === undefined || value === '') ? null : value;
      }
    });

    return JSON.stringify(cleaned);
  }

  // ✨ NEW: Evaluates whether the user made changes comparing to applied filters
  private checkChanges() {
    if (!this.form) return;
    const currentState = this.serializeState(this.form.value);
    
    // Prioritize fetchedSavedFilters if provided by parent, otherwise fallback to local initialization
    const compareState = this.fetchedSavedFilters 
      ? this.serializeState(this.fetchedSavedFilters)
      : this.baseFormStateStr;

    this.isChanged = currentState !== compareState;
    this.cd.markForCheck();
  }

  // ✨ NEW: Checks if ANY custom filters are applied (To toggle the 'Reset Filters' UX icon)
  isActiveFilterPresent(): boolean {
    if (!this.form || !this.config) return false;
    const val = this.form.value;
    
    return this.config.some(ctrl => {
      const v = val[ctrl.key];
      if (ctrl.type === 'range') {
        const min = ctrl.rangeOptions?.min ?? 0;
        const max = ctrl.rangeOptions?.max ?? 100;
        return v && (v.from !== min || v.to !== max);
      } else if (Array.isArray(v)) {
        return v.length > 0;
      } else {
        return v !== null && v !== undefined && v !== '';
      }
    });
  }

  private safelyPatchFormValues(newValues: any): void {
    if (!this.form || !this.config) return;

    this.config.forEach(ctrl => {
      const formControl = this.form.get(ctrl.key);
      if (!formControl) return;

      const newValue = newValues[ctrl.key];
      if (ctrl.type === 'range') {
        const minVal = newValue?.from !== undefined ? newValue.from : (ctrl.rangeOptions?.min ?? 0);
        const maxVal = newValue?.to !== undefined ? newValue.to : (ctrl.rangeOptions?.max ?? 100);
        formControl.setValue({ from: minVal, to: maxVal }, { emitEvent: false });
      } 
      else if (ctrl.type === 'checkbox') {
        const arrValue = Array.isArray(newValue) ? newValue : (newValue ? [newValue] : []);
        formControl.setValue(arrValue, { emitEvent: false });
      } 
      else {
        formControl.setValue(newValue !== undefined ? newValue : null, { emitEvent: false });
      }
    });

    this.form.updateValueAndValidity({ emitEvent: false });
    this.baseFormStateStr = this.serializeState(this.form.value);
    this.checkChanges();
    this.cd.markForCheck();
  }

  @HostListener('document:click')
  closeDropdowns() {
    this.openDropdownKey = null;
  }

  buildForm() {
    const group: any = {};

    this.config.forEach(ctrl => {
      const validators = [];
      if (ctrl.required) validators.push(Validators.required);
      if (ctrl.pattern) validators.push(Validators.pattern(ctrl.pattern));

      const initVal = this.initialValues[ctrl.key] !== undefined ? this.initialValues[ctrl.key] : null;
      const isDisabled = !!(
        ctrl.isDisabled ||
        (ctrl.type === 'range' && ctrl.rangeOptions?.isDisabled)
      );

      if (ctrl.type === 'range') {
        const minVal = initVal?.from ?? ctrl.rangeOptions?.min ?? 0;
        const maxVal = initVal?.to ?? ctrl.rangeOptions?.max ?? 100;
        group[ctrl.key] = this.fb.group({
          from: [{ value: minVal, disabled: isDisabled }],
          to: [{ value: maxVal, disabled: isDisabled }]
        });
      } else if (ctrl.type === 'checkbox') {
        group[ctrl.key] = new FormControl({ value: initVal || [], disabled: isDisabled }, validators);
      } else {
        group[ctrl.key] = new FormControl({ value: initVal, disabled: isDisabled }, validators);
      }
    });

    this.form = this.fb.group(group);

    // Initial state setup for detection
    this.baseFormStateStr = this.serializeState(this.form.value);

    if (this.formSubscription) this.formSubscription.unsubscribe();

    // Listen to changes to toggle Apply/Reset buttons continuously
    this.formSubscription = this.form.valueChanges.pipe(
      debounceTime(150)
    ).subscribe(() => {
      this.checkChanges();
    });

    this.checkChanges();
  }

  onReset() {
    if (!this.allowResetWithoutFilters && !this.isActiveFilterPresent()) return;
    
    this.form.reset();
    
    // Updating local base state prevents false triggers post-reset
    this.fetchedSavedFilters = null;
    this.baseFormStateStr = this.serializeState(this.form.value);
    this.checkChanges();
    
    this.reset.emit();
  }

  ngOnDestroy(): void {
    if (this.formSubscription) this.formSubscription.unsubscribe();
    this.searchSubject.unsubscribe();
  }

  onApplyFilters() {
    if (this.form.valid) {
      this.filtersChange.emit(this.form.value);
      
      // Sync state so the button returns to disabled
      this.fetchedSavedFilters = { ...this.form.value }; 
      this.baseFormStateStr = this.serializeState(this.form.value);
      this.checkChanges();
      
      this.openDropdownKey = null;
    } else {
      this.form.markAllAsTouched();
    }
  }

  // --- Existing Logic Kept Exactly as is below ---

  toggleDropdown(key: string, event: Event) {
    event.stopPropagation();
    this.openDropdownKey = this.openDropdownKey === key ? null : key;
  }

  removeItemForStringTypeDropdown(key: any, labelToRemove: string, event: Event): void {
    this.selectOption(key, labelToRemove, event);
  }

  removeItemForObjectFormatDropdown(key: string, labelToRemove: string, event: Event): void {
    event.stopPropagation(); 
    const currentValues = this.form.get(key)?.value;
    if (!Array.isArray(currentValues)) return;

    const ctrl = this.config.find(c => c.key === key);
    if (!ctrl) return;

    let updatedValues: any[];
    if (ctrl.type === 'select-object') {
      const itemToMatch = ctrl.objectOptions?.find((opt: any) => opt[ctrl.displayKey || 'name'] === labelToRemove);
      const idToRemove = itemToMatch ? itemToMatch[ctrl.valueKey || 'id'] : null;
      updatedValues = currentValues.filter(val => val !== idToRemove);
    } else {
      updatedValues = currentValues.filter(val => val !== labelToRemove);
    }

    const finalValue = updatedValues.length > 0 ? updatedValues : null;
    this.form.get(key)?.setValue(finalValue);
    this.form.get(key)?.markAsDirty();
    this.cd.markForCheck();
  }

  selectOption(key: string, value: any, event: Event) {
    event.stopPropagation(); 
    const control = this.form.get(key);
    const rawValue = control?.value;

    let updatedValues = Array.isArray(rawValue) ? [...rawValue] : (rawValue ? [rawValue] :[]);

    if (value === null) {
      control?.setValue(null);
    } else {
      const index = updatedValues.indexOf(value);
      if (index > -1) {
        updatedValues.splice(index, 1);
      } else {
        updatedValues.push(value); 
      }
      control?.setValue(updatedValues); 
    }
    control?.markAsTouched();
    this.cd.markForCheck(); 
  }

  isSelected(key: string, opt: any): boolean {
    const values = this.form.get(key)?.value;
    return Array.isArray(values) ? values.includes(opt) : values === opt;
  }

  getSelectedLabel(ctrl: any): string {
    const val = this.form.get(ctrl.key)?.value;
    if (val === null || val === undefined || val === '' || (Array.isArray(val) && val.length === 0)) {
      return ctrl.placeholder || 'Select...';
    }
    if (ctrl.type === 'select-string') {
      return Array.isArray(val) ? val.join(', ') : val;
    }
    if (ctrl.type === 'select-object') {
      if (Array.isArray(val) && val.length > 0) {
        const selectedNames = ctrl.objectOptions
          ?.filter((opt: any) => val.includes(opt[ctrl.valueKey || 'id']))
          .map((opt: any) => opt[ctrl.displayKey || 'name']);
        return selectedNames?.length > 0 ? selectedNames.join(', ') : (ctrl.placeholder || 'Select...');
      }
    }
    return ctrl.placeholder || 'Select...';
  }

  getSelectedNamesArray(ctrl: any): string[] {
    const val = this.form.get(ctrl.key)?.value;
    if (!val || (Array.isArray(val) && val.length === 0)) return [];
    if (ctrl.type === 'select-string') {
      return Array.isArray(val) ? val : [val];
    }
    if (ctrl.type === 'select-object') {
      if (Array.isArray(val)) {
        return ctrl.objectOptions
          ?.filter((opt: any) => val.includes(opt[ctrl.valueKey || 'id']))
          .map((opt: any) => opt[ctrl.displayKey || 'name']) || [];
      }
    }
    return [];
  }

  getLabelsArray(ctrl: any): string[] {
    const value = this.getSelectedLabel(ctrl);
    if (!value || !this.form.get(ctrl.key)?.value) return [];
    return value.split(',').map(item => item.trim()).filter(item => item !== '');
  }

  clearSelection(key: string, event: Event): void {
    event.stopPropagation();
    this.form.get(key)?.setValue(null);
    this.form.get(key)?.markAsDirty();
  }

  onCheckboxChange(key: string, value: any, event: Event) {
    const isChecked = (event.target as HTMLInputElement).checked;
    const control = this.form.get(key);
    let currentArr = Array.isArray(control?.value) ? [...control!.value] : [];

    if (isChecked) {
      if (!currentArr.includes(value)) currentArr = [...currentArr, value];
    } else {
      currentArr = currentArr.filter((item: any) => item !== value);
    }
    control?.setValue(currentArr);
    control?.markAsTouched();
  }

  getRangeGroup(key: string): FormGroup {
    return this.form.get(key) as FormGroup;
  }

  onRangeInput(key: string, options: any, type: 'from' | 'to') {
    const group = this.getRangeGroup(key);
    if (!group) return;

    const fromCtrl = group.get('from');
    const toCtrl = group.get('to');

    const min = Number(options?.min ?? 0);
    const max = Number(options?.max ?? 100);

    let fromVal = Number(fromCtrl?.value ?? min);
    let toVal = Number(toCtrl?.value ?? max);

    fromVal = Math.max(min, Math.min(fromVal, max));
    toVal = Math.max(min, Math.min(toVal, max));

    if (type === 'from' && fromVal > toVal) {
      fromVal = toVal; 
      fromCtrl?.setValue(fromVal, { emitEvent: false });
    } else if (type === 'to' && toVal < fromVal) {
      toVal = fromVal; 
      toCtrl?.setValue(toVal, { emitEvent: false });
    } else {
      if (type === 'from' && Number(fromCtrl?.value) !== fromVal) {
        fromCtrl?.setValue(fromVal, { emitEvent: false });
      }
      if (type === 'to' && Number(toCtrl?.value) !== toVal) {
        toCtrl?.setValue(toVal, { emitEvent: false });
      }
    }
  }

  getRangeMinPercent(key: string, options: any): number {
    const min = Number(options?.min ?? 0);
    const max = Number(options?.max ?? 100);
    const fromVal = Number(this.getRangeGroup(key)?.get('from')?.value ?? min);
    return this.calculateSafePercentage(fromVal, min, max);
  }

  getRangeMaxPercent(key: string, options: any): number {
    const min = Number(options?.min ?? 0);
    const max = Number(options?.max ?? 100);
    const toVal = Number(this.getRangeGroup(key)?.get('to')?.value ?? max);
    return this.calculateSafePercentage(toVal, min, max);
  }

  getRangeWidth(key: string, options: any): number {
    const minPercent = this.getRangeMinPercent(key, options);
    const maxPercent = this.getRangeMaxPercent(key, options);
    const calculatedWidth = maxPercent - minPercent;
    return Math.max(1.5, calculatedWidth);
  }

  private calculateSafePercentage(value: number, min: number, max: number): number {
    if (min === max) return 0; 
    const clampedValue = Math.max(min, Math.min(value, max));
    const percentage = ((clampedValue - min) / (max - min)) * 100;
    return Math.max(0, Math.min(percentage, 100));
  }

  formatRangeLabel(ctrl: FilterConfig): string {
    const group = this.getRangeGroup(ctrl.key);
    const from = group.get('from')?.value ?? 0;
    const to = group.get('to')?.value ?? 100;
    
    if (ctrl.unit === 'days') {
      return `${from} ${from === 1 ? 'day' : 'days'} - ${to} ${to === 1 ? 'day' : 'days'}`;
    } 
    if (ctrl.unit === '%') {
      return `${from}% - ${to}%`;
    }
    return `${from} - ${to}`;
  }

  getDropdownOptions(ctrl: FilterConfig): any[] {
    const options = (ctrl.type === 'select-object' ? ctrl.objectOptions : ctrl.options) || [];
    const searchValue = this.localSearchValues[ctrl.key]?.trim().toLowerCase();
    if (!ctrl.searchOnJson || !searchValue) return options;

    return options.filter(option => {
      const label = ctrl.type === 'select-object' ? option?.[ctrl.displayKey || 'name'] : option;
      return String(label ?? '').toLowerCase().includes(searchValue);
    });
  }

  async executeSearch(key: string, searchValue: string) {
    if (this.config.find(ctrl => ctrl.key === key)?.searchOnJson) {
      this.localSearchValues[key] = searchValue;
      this.cd.markForCheck();
      return;
    }

    this.loading = true;
    try {
      const payload = { ...this.filterPayloadTemplate[this.screenName] };
      if (searchValue && searchValue.trim() !== '') {
        payload['searchField'] = key;
        payload['search'] = searchValue.trim(); 
      }

      let apiCall$;
      if (this.screenName === 'PM_VIEW') {
        apiCall$ = this._staffingService.getAllPmViewFilterData(payload); 
      } else if (this.screenName === 'SR_VIEW') {
        apiCall$ = this._staffingService.getAllFiltersData(payload);
      } else if (this.screenName === 'MAPPED_CANDIDATE') {
        payload['srNumber'] = this.srNumber;
        apiCall$ = this._staffingService.getAllMapppedCandidteFilterData(payload);
      } else {
        apiCall$ = this._staffingService.getAllBenchEmployeeFilterData(payload);
      }
      
      const response = await firstValueFrom(apiCall$) as ApiResponse<any>;
      if (response && response.statusCode === 200) {
        const controlToUpdate = this.config.find(c => c.key === key);
        if (controlToUpdate) {
          if (controlToUpdate.type === 'select-string') {
            controlToUpdate.options = response.data[key] || [];
          } else if (controlToUpdate.type === 'select-object') {
            controlToUpdate.objectOptions = response.data[key] || [];
          }
        }
      }
    } catch (err) {
      console.error(`Error searching ${key}:`, err);
    } finally {
      this.loading = false;
    }
  }

  onSearchInput(key: string, event: Event) {
    const inputElement = event.target as HTMLInputElement;
    if (this.config.find(ctrl => ctrl.key === key)?.searchOnJson) {
      this.executeSearch(key, inputElement.value);
      return;
    }

    setTimeout(() => {
      this.searchSubject.next({ key, value: inputElement.value });
    }, 500);
  }

  onDateSelected(event: any, key: string) {
    this.form.get(key)?.setValue(event);
    this.form.get(key)?.markAsDirty();
    this.cd.markForCheck();
  }

  isOptionSelected(key: string, value: any): boolean {
    if (!this.form) return false;
    const control = this.form.get(key);
    if (control && Array.isArray(control.value)) {
      return control.value.includes(value);
    }
    return false;
  }
}
